// A balança deste computador, uma conexão só para o painel inteiro: o popup
// do produto, a pesagem rápida e a tela de configuração leem daqui.
//
// - lerAgora(): uma leitura (o que a balança diz neste instante).
// - lerPesoFirme(): espera o peso parar (~1 s) — botão "Ler balança".
// - escutar(fn): leituras a cada ~300 ms enquanto alguém escuta — modo automático.
import { abrirCabo, infoDaPorta, portaSalva, webSerialDisponivel } from "./cabo";
import { aoMudarConfig, lerConfig, salvarConfig } from "./config";
import { COMBINACOES, pesoEstavel } from "./protocolo";
import { conexaoSimulada } from "./simulador";

const INTERVALO_MS = 300;

let conexao = null;
let abrindo = null;
const ouvintes = new Set();
let laco = null;
let historico = [];

aoMudarConfig(() => { desconectar(); });

async function conectar() {
  if (conexao) return conexao;
  if (abrindo) return abrindo;
  const cfg = lerConfig();
  abrindo = (async () => {
    if (cfg.conexao === "simulador") return conexaoSimulada;
    if (!webSerialDisponivel()) throw erro("sem_serial");
    const port = await portaSalva(cfg.porta);
    if (!port) throw erro("sem_porta");
    port.addEventListener?.("disconnect", () => { conexao = null; });
    return abrirCabo(port, cfg);
  })();
  try {
    conexao = await abrindo;
    return conexao;
  } finally {
    abrindo = null;
  }
}

function erro(codigo) {
  const e = new Error(codigo);
  e.codigo = codigo;
  return e;
}

export async function desconectar() {
  const c = conexao;
  conexao = null;
  historico = [];
  if (c) await c.fechar();
}

export async function lerAgora() {
  try {
    const c = await conectar();
    const l = await c.pedir();
    const leitura = { ...l, em: Date.now() };
    if (leitura.estado === "sem_resposta" && conexao === c) await desconectar();
    return leitura;
  } catch (e) {
    return { estado: e.codigo || "sem_resposta", kg: null, em: Date.now() };
  }
}

/** Espera o peso firmar; devolve a leitura firme ou a última que conseguiu. */
export async function lerPesoFirme({ limiteMs = 4000 } = {}) {
  const inicio = Date.now();
  const hist = [];
  let ultima = null;
  while (Date.now() - inicio < limiteMs) {
    ultima = await lerAgora();
    hist.push(ultima);
    if (["sem_serial", "sem_porta", "sem_resposta", "negativo", "sobrecarga"].includes(ultima.estado)) return ultima;
    const firme = pesoEstavel(hist);
    if (firme != null) return { estado: "ok", kg: firme, firme: true };
    await new Promise((r) => setTimeout(r, INTERVALO_MS));
  }
  return ultima || { estado: "sem_resposta", kg: null };
}

/** Leituras contínuas para o modo automático. fn({ leitura, firmeKg }). */
export function escutar(fn) {
  ouvintes.add(fn);
  if (!laco) {
    laco = true;
    (async function rodar() {
      while (ouvintes.size) {
        const leitura = await lerAgora();
        historico = [...historico, leitura].slice(-12);
        const firmeKg = pesoEstavel(historico);
        ouvintes.forEach((f) => f({ leitura, firmeKg }));
        await new Promise((r) => setTimeout(r, INTERVALO_MS));
      }
      laco = null;
    })();
  }
  return () => { ouvintes.delete(fn); };
}

/** Pede ao Windows/navegador para escolher a porta (precisa de um clique). */
export async function escolherPorta() {
  if (!webSerialDisponivel()) throw erro("sem_serial");
  const port = await navigator.serial.requestPort();
  await desconectar();
  const info = infoDaPorta(port);
  const cfg = lerConfig();
  salvarConfig({ ...cfg, porta: { ...info, nome: nomeDaPorta(info) } });
  return port;
}

export function nomeDaPorta(info) {
  if (!info || info.usbVendorId == null) return "Porta serial";
  const hex = (n) => n.toString(16).padStart(4, "0").toUpperCase();
  return `USB ${hex(info.usbVendorId)}:${hex(info.usbProductId ?? 0)}`;
}

/**
 * Testa as combinações de velocidade/paridade até a balança responder.
 * Devolve { combinacao, leitura } ou null. onPasso(i, total, combinacao).
 */
export async function detectar(onPasso) {
  await desconectar();
  const cfg = lerConfig();
  const port = await portaSalva(cfg.porta);
  if (!port) throw erro("sem_porta");
  for (let i = 0; i < COMBINACOES.length; i += 1) {
    const comb = COMBINACOES[i];
    onPasso && onPasso(i + 1, COMBINACOES.length, comb);
    let c = null;
    try {
      c = await abrirCabo(port, { ...cfg, ...comb, pedirPeso: true });
      for (let t = 0; t < 2; t += 1) {
        const l = await c.pedir();
        if (l && !["invalido", "sem_resposta"].includes(l.estado)) {
          await c.fechar();
          return { combinacao: comb, leitura: l };
        }
      }
    } catch {
      /* combinação recusada pela porta: tenta a próxima */
    }
    if (c) await c.fechar();
  }
  return null;
}

export const MENSAGEM_CONEXAO = {
  sem_serial: "Este navegador não fala com a balança. Use o Núcleo Desktop atualizado, o Chrome ou o Edge no computador.",
  sem_porta: "Escolha a porta da balança em Configurações → Sistema → Integração → Balança.",
  sem_resposta: "A balança não respondeu. Confira o cabo, se ela está ligada e a configuração da porta.",
};
